import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import {
  approveReview,
  fetchListings,
  fetchPendingReviews,
  updateListing,
  type Listing,
  type Review,
} from '../../features/marketplace/api';

type AdminTab = 'listings' | 'reviews';

export function MarketplaceAdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listings, setListings] = useState<Listing[]>([]);
  const [pendingReviews, setPendingReviews] = useState<Review[]>([]);
  const [reviewSelections, setReviewSelections] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [updating, setUpdating] = useState<string | null>(null);
  const [reviewUpdating, setReviewUpdating] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AdminTab>('listings');

  useEffect(() => {
    loadAllListings();
    loadPendingReviews();
  }, []);

  async function loadAllListings() {
    try {
      setLoading(true);
      const data = await fetchListings(100);
      setListings(data || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load listings');
      setListings([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadPendingReviews() {
    try {
      const reviews = await fetchPendingReviews(100);
      setPendingReviews(reviews || []);
      const defaults: Record<string, string> = {};
      (reviews || []).forEach((r) => {
        defaults[r.id] = r.classification || 'normal';
      });
      setReviewSelections(defaults);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pending reviews');
      setPendingReviews([]);
    }
  }

  async function handleApprove(listingId: string) {
    try {
      setUpdating(listingId);
      setError(null);
      await updateListing(listingId, { status: 'approved' });
      await loadAllListings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve listing');
    } finally {
      setUpdating(null);
    }
  }

  async function handleReject(listingId: string) {
    try {
      setUpdating(listingId);
      setError(null);
      await updateListing(listingId, { status: 'rejected' });
      await loadAllListings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject listing');
    } finally {
      setUpdating(null);
    }
  }

  const filteredListings = statusFilter === 'all'
    ? listings
    : listings.filter(l => l.status === statusFilter);

  const pendingCount = listings.filter(l => l.status === 'pending_approval').length;
  const approvedCount = listings.filter(l => l.status === 'approved').length;
  const rejectedCount = listings.filter(l => l.status === 'rejected').length;
  const hasPendingReviews = pendingReviews.length > 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900">
        <div className="text-center text-slate-600 dark:text-slate-400 py-12">Loading listings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">Admin Approval Panel</h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">Review and approve/reject marketplace listings and customer reviews</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate('/marketplace')}
              variant="secondary"
            >
              Browse Listings
            </Button>
            {user?.role === 'seller' && (
              <Button
                onClick={() => navigate('/marketplace/seller')}
                variant="secondary"
              >
                Seller Dashboard
              </Button>
            )}
          </div>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('listings')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'listings'
              ? 'bg-brand-600 text-white'
              : 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600'
              }`}
          >
            Listing Approval
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === 'reviews'
              ? 'bg-brand-600 text-white'
              : 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600'
              }`}
          >
            Review Approval
          </button>
        </div>

        {error && (
          <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400 mb-6">
            {error}
          </div>
        )}

        {activeTab === 'reviews' ? (
          /* Pending Reviews Moderation */
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 mb-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Pending Customer Reviews</h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">Assign a classification and approve to publish.</p>
              </div>
              <Button variant="secondary" onClick={loadPendingReviews}>Refresh</Button>
            </div>

            {hasPendingReviews ? (
              <div className="space-y-3">
                {pendingReviews.map((review) => (
                  <div key={review.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-slate-50 dark:bg-slate-900/40">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          Booking: <span className="font-semibold text-slate-900 dark:text-slate-100">{review.bookingId}</span>
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          Listing: <span className="font-semibold text-slate-900 dark:text-slate-100">{review.listingId}</span>
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                          Buyer: {review.buyerAlias || 'n/a'} • Rating: {review.rating}/5
                        </div>
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {new Date(review.createdAt).toLocaleString()}
                      </div>
                    </div>
                    <div className="mt-3 text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-3">
                      {review.comment}
                    </div>
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Classification</label>
                      <select
                        value={reviewSelections[review.id] || 'normal'}
                        onChange={(e) => setReviewSelections((prev) => ({ ...prev, [review.id]: e.target.value }))}
                        className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                      >
                        <option value="normal">Normal</option>
                        <option value="observable">Observable</option>
                        <option value="advance">Advance</option>
                        <option value="superior">Superior</option>
                      </select>
                      <Button
                        size="sm"
                        disabled={reviewUpdating === review.id}
                        onClick={async () => {
                          try {
                            setReviewUpdating(review.id);
                            setError(null);
                            await approveReview(review.id, reviewSelections[review.id] || 'normal');
                            await loadPendingReviews();
                          } catch (err) {
                            setError(err instanceof Error ? err.message : 'Failed to approve review');
                          } finally {
                            setReviewUpdating(null);
                          }
                        }}
                      >
                        {reviewUpdating === review.id ? 'Approving...' : 'Approve & Publish'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-600 dark:text-slate-400">No pending reviews.</div>
            )}
          </div>
        ) : (
          <>
            {/* Status Filter */}
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Filter by status:</span>
                <button
                  onClick={() => setStatusFilter('all')}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${statusFilter === 'all'
                    ? 'bg-brand-600 text-white'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                    }`}
                >
                  All ({listings.length})
                </button>
                <button
                  onClick={() => setStatusFilter('pending_approval')}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${statusFilter === 'pending_approval'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                    }`}
                >
                  Pending ({pendingCount})
                </button>
                <button
                  onClick={() => setStatusFilter('approved')}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${statusFilter === 'approved'
                    ? 'bg-green-600 text-white'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                    }`}
                >
                  Approved ({approvedCount})
                </button>
                <button
                  onClick={() => setStatusFilter('rejected')}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${statusFilter === 'rejected'
                    ? 'bg-red-600 text-white'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
                    }`}
                >
                  Rejected ({rejectedCount})
                </button>
              </div>
            </div>

            {/* Listings Grid */}
            {filteredListings.length === 0 ? (
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8 text-center">
                <p className="text-slate-600 dark:text-slate-400">No listings with status: {statusFilter}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {filteredListings.map((listing) => (
                  <div
                    key={listing.id}
                    className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6 hover:border-brand-500 dark:hover:border-slate-600 transition-colors"
                  >
                    <div className="flex gap-6">
                      {/* Image */}
                      {listing.media && listing.media.length > 0 ? (
                        <div className="w-32 h-32 bg-slate-100 dark:bg-slate-900 flex-shrink-0 rounded overflow-hidden">
                          <img
                            src={listing.media[0]}
                            alt={listing.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.parentElement!.innerHTML = '<div class="text-slate-600 text-xs p-2 flex items-center justify-center h-full">No image</div>';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="w-32 h-32 bg-slate-100 dark:bg-slate-900 flex-shrink-0 rounded flex items-center justify-center">
                          <svg className="w-12 h-12 text-slate-400 dark:text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}

                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-1">{listing.title}</h3>
                            <p className="text-sm text-slate-600 dark:text-slate-400">by {listing.sellerAlias}</p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {/* Status Badge */}
                            <span
                              className={`px-3 py-1 rounded text-sm font-semibold ${listing.status === 'approved'
                                ? 'bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-800 text-green-700 dark:text-green-400'
                                : listing.status === 'rejected'
                                  ? 'bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400'
                                  : 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-800 text-yellow-700 dark:text-yellow-400'
                                }`}
                            >
                              {listing.status === 'pending_approval' ? 'Pending Approval' : listing.status.charAt(0).toUpperCase() + listing.status.slice(1)}
                            </span>
                            {/* Edited Badge */}
                            {new Date(listing.updatedAt).getTime() - new Date(listing.createdAt).getTime() > 1000 && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-brand-100 dark:bg-brand-900/30 border border-brand-300 dark:border-brand-800 text-brand-700 dark:text-brand-400">
                                ✏️ Edited
                              </span>
                            )}
                          </div>
                        </div>

                        <p className="text-slate-700 dark:text-slate-300 mb-4 line-clamp-2">{listing.description}</p>

                        <div className="flex items-center justify-between mb-4">
                          <div className="text-2xl font-bold text-brand-600 dark:text-brand-400">
                            {listing.currency} {(listing.priceCents / 100).toFixed(2)}
                          </div>
                          <div className="text-xs text-slate-600 dark:text-slate-500 text-right">
                            <div>Listed: {new Date(listing.createdAt).toLocaleDateString()}</div>
                            {new Date(listing.updatedAt).getTime() - new Date(listing.createdAt).getTime() > 1000 && (
                              <div className="text-brand-600 dark:text-brand-400">Last edited: {new Date(listing.updatedAt).toLocaleDateString()} {new Date(listing.updatedAt).toLocaleTimeString()}</div>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={() => navigate(`/marketplace/admin/listing/${listing.id}`)}
                            className="bg-blue-700 hover:bg-blue-600"
                          >
                            👁️ View Details
                          </Button>

                          {listing.status === 'pending_approval' && (
                            <>
                              <Button
                                onClick={() => handleApprove(listing.id)}
                                disabled={updating === listing.id}
                                className="bg-green-700 hover:bg-green-600"
                              >
                                {updating === listing.id ? 'Processing...' : '✓ Approve'}
                              </Button>
                              <Button
                                onClick={() => handleReject(listing.id)}
                                disabled={updating === listing.id}
                                className="bg-red-700 hover:bg-red-600"
                              >
                                {updating === listing.id ? 'Processing...' : '✕ Reject'}
                              </Button>
                            </>
                          )}

                          {listing.status === 'rejected' && (
                            <Button
                              onClick={() => handleApprove(listing.id)}
                              disabled={updating === listing.id}
                              className="bg-green-700 hover:bg-green-600"
                            >
                              {updating === listing.id ? 'Processing...' : 'Re-approve'}
                            </Button>
                          )}

                          {listing.status === 'approved' && (
                            <Button
                              onClick={() => handleReject(listing.id)}
                              disabled={updating === listing.id}
                              className="bg-red-700 hover:bg-red-600"
                            >
                              {updating === listing.id ? 'Processing...' : 'Revoke Approval'}
                            </Button>
                          )}
                        </div>

                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}


      </div>
    </div>
  );
}
